import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useAnimation } from 'motion/react';

/**
 * The req 006 step-7 depth lock (H32) as a shared hook.
 *
 * Buttons are border-only and static at rest; the *click* is the depth
 * gesture. Press lifts 1.5px and — for the chromatic roles — inverts (fills
 * the role colour, snaps the border transparent, role-tinted-white text);
 * release fades back over a keyframed curve with a hold-at-full prefix. Neutral
 * roles get the same lift + a quiet accent fill; `lift-plain` is the lift only.
 *
 * Extracted from `button.tsx` on its second consumer (`AddItemButton`) per the
 * project's extract-on-second-use rule. Spec + rationale:
 * design/exploration/008-req-006-formal-session.md §"Depth system".
 */

// --- H32 timing (step-7 lock; H23's 0.8× speed scaling) ---
const LIFT_Y = -1.5;
const LIFT_DURATION = 0.07; // 87ms after the 0.8× speed scale
const EXIT_DURATION = 0.18; // 225ms after the scale — the colour fade-out
const BORDER_FADE_DURATION = 0.1; // 125ms after the scale — border return
const SPEED_SCALE = 0.8;
const MIN_HOLD_MS = 80; // pad fast clicks so the peak state is processable
const EXIT_HOLD_PREFIX_FRAC = 0.35; // ~79ms hold-at-full baked into the fade
const PRESS_SHADOW = '0 4px 9px -2px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04)';
const REST_SHADOW = '0 0 0 0 rgba(0, 0, 0, 0)';

// sRGB rgba equivalents of the J / DK-B role tokens. **Why rgba, not the oklch
// tokens:** motion v12 forces the WAAPI fallback when colour keyframes contain
// oklch literals, which flickers on partial-alpha fades. Keeping the animated
// values as rgba holds the animation on motion's JS path. Locked rest colours
// stay oklch (the cva classes); only the *animated* values are rgba. Light +
// dark sets switch on the `.dark` class on <html> — the cascade mechanism — so
// this works in Storybook (class toggled by the theme decorator) and in the
// app alike, with no dependency on the React ThemeProvider. See exploration
// note 008 §"Depth system" §"Technical learnings" #1.
// Per role: `restText`/`restBorder` are the at-rest border-only colours;
// `fill` is the pressed background; `pressText` is the text on the fill; `fillT`
// is `fill` at alpha 0 (the transparent endpoint of the bg fade + the
// border-vanish target). Chromatic roles (primary/destructive) use the SAME
// colour for rest border, rest text, and fill — the role colour "intensifies"
// into a fill. The neutral role (outline/secondary) decouples them: a readable
// near-fg rest text + a mid-grey border + a genuinely dark fill, so neutral
// buttons read clearly at rest but still get a dark press + border-vanish.
// `ghost` shares the neutral fill + inverted text but has a transparent rest
// border (it stays borderless at rest — emphasis lives in the rest border's
// presence, which ghost forgoes; the press feedback is the same neutral fill so
// it reads consistently across themes, unlike a near-white accent fill which
// vanished on a light background).
const INVERT_COLORS = {
  light: {
    primary: { restText: 'rgba(40, 111, 118, 1)', restBorder: 'rgba(40, 111, 118, 1)', fill: 'rgba(40, 111, 118, 1)', fillT: 'rgba(40, 111, 118, 0)', pressText: 'rgba(246, 251, 252, 1)' },
    destructive: { restText: 'rgba(148, 75, 67, 1)', restBorder: 'rgba(148, 75, 67, 1)', fill: 'rgba(148, 75, 67, 1)', fillT: 'rgba(148, 75, 67, 0)', pressText: 'rgba(253, 249, 248, 1)' },
    neutral: { restText: 'rgba(23, 23, 23, 1)', restBorder: 'rgba(113, 113, 113, 1)', fill: 'rgba(72, 72, 72, 1)', fillT: 'rgba(72, 72, 72, 0)', pressText: 'rgba(250, 250, 250, 1)' },
    ghost: { restText: 'rgba(23, 23, 23, 1)', restBorder: 'rgba(0, 0, 0, 0)', fill: 'rgba(72, 72, 72, 1)', fillT: 'rgba(72, 72, 72, 0)', pressText: 'rgba(250, 250, 250, 1)' },
  },
  dark: {
    primary: { restText: 'rgba(66, 134, 142, 1)', restBorder: 'rgba(66, 134, 142, 1)', fill: 'rgba(66, 134, 142, 1)', fillT: 'rgba(66, 134, 142, 0)', pressText: 'rgba(246, 251, 252, 1)' },
    destructive: { restText: 'rgba(174, 98, 90, 1)', restBorder: 'rgba(174, 98, 90, 1)', fill: 'rgba(174, 98, 90, 1)', fillT: 'rgba(174, 98, 90, 0)', pressText: 'rgba(253, 249, 248, 1)' },
    neutral: { restText: 'rgba(232, 232, 232, 1)', restBorder: 'rgba(99, 99, 99, 1)', fill: 'rgba(158, 158, 158, 1)', fillT: 'rgba(158, 158, 158, 0)', pressText: 'rgba(22, 22, 22, 1)' },
    ghost: { restText: 'rgba(232, 232, 232, 1)', restBorder: 'rgba(0, 0, 0, 0)', fill: 'rgba(158, 158, 158, 1)', fillT: 'rgba(158, 158, 158, 0)', pressText: 'rgba(22, 22, 22, 1)' },
  },
} as const;

// Quiet neutral fill (`--accent` flattened to rgba) for the lift-fill kind.
const ACCENT = {
  light: { fill: 'rgba(242, 242, 242, 1)', fillT: 'rgba(242, 242, 242, 0)' },
  dark: { fill: 'rgba(34, 34, 34, 1)', fillT: 'rgba(34, 34, 34, 0)' },
} as const;

export type GestureKind = 'invert' | 'lift-fill' | 'lift-plain';

// Track the resolved theme via the `.dark` class on <html> (the token-cascade
// mechanism). Decoupled from the React ThemeProvider so the gesture's animated
// rgba is correct in Storybook (class toggled by the theme decorator) too.
export function useResolvedDark(): boolean {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDark(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

export interface PressGesture {
  animate: ReturnType<typeof useAnimation>;
  initial: Record<string, string | number>;
  focusTintClass: string;
  handlers: {
    onPointerDown: () => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onPointerLeave: () => void;
    // Element-typed as `HTMLElement` (not `HTMLButtonElement`) so the one hook
    // serves both the button consumers (`Button`, `AddItemButton`) and the
    // anchor consumer (`ButtonLink`). The handlers read only `e.key`, never an
    // element-specific member, so widening the param is behaviour-neutral and
    // type-safe at every call site (a button/anchor event satisfies the wider
    // `HTMLElement` param contravariantly).
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
    onKeyUp: (e: KeyboardEvent<HTMLElement>) => void;
  };
}

// The H32 press cycle. `invert` cycles the role colour (fill + inverted text +
// border-vanish); `lift-fill` lifts + fades a quiet accent fill; `lift-plain`
// lifts only.
export type InvertRole = 'primary' | 'destructive' | 'neutral' | 'ghost';
export function usePressGesture(kind: GestureKind, role?: InvertRole): PressGesture {
  const dark = useResolvedDark();
  const controls = useAnimation();
  const pressedAtRef = useRef<number | null>(null);
  const exitTimeoutRef = useRef<number | null>(null);
  const keyPressedRef = useRef(false);
  // Press-cycle flag: the focus-tint CSS uses `!important` to beat the inline
  // colours, which would clobber the invert during keyboard activation (focus
  // stays visible while Enter/Space is held). We drop the focus-tint class for
  // the duration of the cycle so the rule simply doesn't match. sessionRef
  // guards against a stale completion callback from an interrupted press.
  const [isInPressCycle, setIsInPressCycle] = useState(false);
  const sessionRef = useRef(0);

  const theme = dark ? 'dark' : 'light';
  const invert = kind === 'invert' && role ? INVERT_COLORS[theme][role] : null;
  const fill = kind === 'lift-fill' ? ACCENT[theme] : null;

  const liftDuration = LIFT_DURATION / SPEED_SCALE;
  const exitDuration = EXIT_DURATION / SPEED_SCALE;
  const borderFadeIn = BORDER_FADE_DURATION / SPEED_SCALE;

  const restState: Record<string, string | number> = { y: 0, boxShadow: REST_SHADOW };
  if (invert) {
    restState.backgroundColor = invert.fillT;
    restState.color = invert.restText;
    restState.borderColor = invert.restBorder;
  } else if (fill) {
    restState.backgroundColor = fill.fillT;
  }

  const playEnter = () => {
    if (invert) {
      void controls.start({
        y: LIFT_Y,
        boxShadow: PRESS_SHADOW,
        backgroundColor: invert.fill,
        color: invert.pressText,
        borderColor: invert.fillT,
        transition: {
          default: { duration: liftDuration, ease: 'linear' },
          backgroundColor: { duration: 0 },
          color: { duration: 0 },
          borderColor: { duration: 0 },
        },
      });
    } else if (fill) {
      void controls.start({
        y: LIFT_Y,
        boxShadow: PRESS_SHADOW,
        backgroundColor: fill.fill,
        transition: {
          default: { duration: liftDuration, ease: 'linear' },
          backgroundColor: { duration: 0 },
        },
      });
    } else {
      void controls.start({
        y: LIFT_Y,
        boxShadow: PRESS_SHADOW,
        transition: { duration: liftDuration, ease: 'linear' },
      });
    }
  };

  const playExit = () => {
    const mySession = sessionRef.current;
    const onExitComplete = () => {
      if (sessionRef.current === mySession) setIsInPressCycle(false);
    };

    if (invert) {
      void controls
        .start({
          y: 0,
          boxShadow: REST_SHADOW,
          backgroundColor: [invert.fill, invert.fill, invert.fillT],
          color: [invert.pressText, invert.pressText, invert.restText],
          borderColor: invert.restBorder,
          transition: {
            default: { duration: liftDuration, ease: 'linear' },
            backgroundColor: { duration: exitDuration, times: [0, EXIT_HOLD_PREFIX_FRAC, 1], ease: 'linear' },
            color: { duration: exitDuration, times: [0, EXIT_HOLD_PREFIX_FRAC, 1], ease: 'linear' },
            borderColor: { duration: borderFadeIn, delay: exitDuration, ease: 'linear' },
          },
        })
        .then(onExitComplete);
    } else if (fill) {
      void controls
        .start({
          y: 0,
          boxShadow: REST_SHADOW,
          backgroundColor: [fill.fill, fill.fill, fill.fillT],
          transition: {
            default: { duration: liftDuration, ease: 'linear' },
            backgroundColor: { duration: exitDuration, times: [0, EXIT_HOLD_PREFIX_FRAC, 1], ease: 'linear' },
          },
        })
        .then(onExitComplete);
    } else {
      void controls
        .start({
          y: 0,
          boxShadow: REST_SHADOW,
          transition: { duration: liftDuration, ease: 'linear' },
        })
        .then(onExitComplete);
    }
  };

  const onPressStart = () => {
    if (exitTimeoutRef.current != null) {
      window.clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
    pressedAtRef.current = Date.now();
    sessionRef.current += 1;
    setIsInPressCycle(true);
    playEnter();
  };

  const onPressEnd = () => {
    if (pressedAtRef.current == null) return;
    const elapsed = Date.now() - pressedAtRef.current;
    pressedAtRef.current = null;
    const remainingMinHold = Math.max(0, MIN_HOLD_MS - elapsed);
    if (remainingMinHold === 0) {
      playExit();
    } else {
      exitTimeoutRef.current = window.setTimeout(() => {
        exitTimeoutRef.current = null;
        playExit();
      }, remainingMinHold);
    }
  };

  // Keyboard parity — native <button> fires click on Enter/Space but motion's
  // pointer handlers are silent on keyboard. Mirror the press on keydown/keyup
  // and gate Space's auto-repeat keydown stream.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (keyPressedRef.current) return;
    keyPressedRef.current = true;
    onPressStart();
  };
  const onKeyUp = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    keyPressedRef.current = false;
    onPressEnd();
  };

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current != null) window.clearTimeout(exitTimeoutRef.current);
    };
  }, []);

  // Re-snap the rest colours when the resolved theme flips. motion's `initial`
  // only lands on mount and the controls sit idle between presses, so without
  // this the rest inline colours (text/border/bg) stay at the mount-time theme
  // until the next press re-animates them — on a light→dark or dark→light
  // switch the text would blend into the freshly-themed background. Snap
  // instantly (duration 0) to match the page's instant token re-cascade; routed
  // through controls.start (not .set) per the rapid-sequence corruption note.
  useEffect(() => {
    void controls.start(restState, { duration: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire only on theme flip; restState is read fresh each render
  }, [dark]);

  // Focus-tint only for the chromatic roles, whose rest border shares the
  // --ring hue (the ring would be invisible against the matching border). The
  // neutral role's border is grey, distinct from the teal ring, so it keeps the
  // standard focus-visible ring.
  const focusTintClass =
    invert && !isInPressCycle && (role === 'primary' || role === 'destructive')
      ? role === 'destructive'
        ? 'depth-focus-tint-destructive'
        : 'depth-focus-tint-primary'
      : '';

  return {
    animate: controls,
    initial: restState,
    focusTintClass,
    handlers: { onPointerDown: onPressStart, onPointerUp: onPressEnd, onPointerCancel: onPressEnd, onPointerLeave: onPressEnd, onKeyDown, onKeyUp },
  };
}

export function compose<E>(own: (e: E) => void, user?: (e: E) => void) {
  return (e: E) => {
    own(e);
    user?.(e);
  };
}
